import * as React from "react";
import * as SliderPrimitive from "@radix-ui/react-slider";

import { cn } from "@/lib/utils";

interface SliderProps
  extends React.ComponentPropsWithoutRef<typeof SliderPrimitive.Root> {
  variant?: "default" | "theme-aware";
  /** Classes merged over the variant's for the track, its filled range and the thumb. */
  trackClassName?: string;
  rangeClassName?: string;
  thumbClassName?: string;
}

const Slider = React.forwardRef<
  React.ElementRef<typeof SliderPrimitive.Root>,
  SliderProps
>(({
  className,
  variant = "default",
  trackClassName,
  rangeClassName,
  thumbClassName,
  // The thumb is the element with role="slider": it carries the name and the spoken value.
  "aria-label": ariaLabel,
  "aria-valuetext": ariaValueText,
  ...props
}, ref) => {
  // Check if we're in a theme context
  const isThemeAware = variant === "theme-aware";

  return (
    <SliderPrimitive.Root
      ref={ref}
      className={cn(
        "relative flex w-full touch-none select-none items-center",
        className
      )}
      {...props}
    >
      <SliderPrimitive.Track
        className={cn(
          "relative h-1.5 w-full grow overflow-hidden rounded-full",
          isThemeAware ? "bg-zinc-200 dark:bg-zinc-800" : "bg-zinc-800",
          trackClassName
        )}
      >
        <SliderPrimitive.Range
          className={cn(
            "absolute h-full",
            isThemeAware ? "bg-zinc-900 dark:bg-white" : "bg-white",
            rangeClassName
          )}
        />
      </SliderPrimitive.Track>
      <SliderPrimitive.Thumb
        aria-label={ariaLabel}
        aria-valuetext={ariaValueText}
        className={cn(
          "block h-4 w-4 rounded-full border shadow transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50",
          isThemeAware
            ? "border-zinc-300 bg-white dark:border-zinc-600 dark:bg-zinc-800"
            : "border-gray-400 bg-white",
          thumbClassName
        )}
      />
    </SliderPrimitive.Root>
  );
});
Slider.displayName = SliderPrimitive.Root.displayName;

export { Slider };
